import { useEffect, useState } from 'react';
import { Box, LinearProgress, Stack, Switch, Typography } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { useTenant } from '@/tenant/useTenant';
import { transition } from '@/theme';
import { initials } from '@/utils/initials';
import { citySlug, schemeFor } from '../format';

/** Respects the visitor's motion preference for looping demos. */
function useTicker(steps: number, everyMs: number) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => setStep((n) => (n + 1) % steps), everyMs);
    return () => window.clearInterval(id);
  }, [steps, everyMs]);
  return step;
}

/** How the professional's page shows up in Google (illustrative). */
export function GooglePreview({
  name,
  color,
  host = window.location.hostname,
}: {
  name: string;
  color: string;
  /** Marketplace host shown in the result (defaults to this page's). */
  host?: string;
}) {
  const { displayName } = useTenant();
  const brand = name.trim() || 'Seu nome';
  return (
    <Box
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        bgcolor: '#fff',
        color: '#202124',
        border: `1px solid ${theme.vars.palette.divider}`,
        fontFamily: 'arial, sans-serif',
      })}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 0.75 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: schemeFor(color).light.primary.main,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initials(brand)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1.2, fontFamily: 'inherit' }} noWrap>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#4d5156', fontFamily: 'inherit' }} noWrap>
            https://{host} › personal › {citySlug(brand) || 'seu-nome'}
          </Typography>
        </Box>
      </Stack>
      <Typography
        sx={{ fontSize: 19, color: '#1a0dab', lineHeight: 1.3, fontFamily: 'inherit' }}
        noWrap
      >
        {brand}, personal trainer em São Paulo/SP
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: '#4d5156', mt: 0.5, fontFamily: 'inherit' }}>
        Hipertrofia, emagrecimento e treino funcional · CREF verificado · Aula experimental grátis.
        Atende em Pinheiros e online…
      </Typography>
    </Box>
  );
}

/** Personal trainer + nutritionist on the same student, with the student's consent. */
export function DuoDemo() {
  const step = useTicker(4, 1800);
  const shares = [
    { label: 'Treinos', who: 'Personal', on: true },
    { label: 'Refeições', who: 'Nutricionista', on: step !== 0 },
    { label: 'Medidas', who: 'Os dois', on: step >= 2 },
  ];
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
        <Bubble label="PE" tone="primary" caption="Personal" />
        <Connector active />
        <Bubble label="AN" tone="accent" caption="Aluna" big />
        <Connector active={step !== 0} />
        <Bubble label="NU" tone="secondary" caption="Nutricionista" />
      </Stack>
      <Box
        sx={(theme) => ({
          p: 1.5,
          borderRadius: 2,
          bgcolor: theme.vars.palette.background.subtle,
        })}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          A aluna decide o que cada um vê (LGPD):
        </Typography>
        {shares.map((share) => (
          <Stack
            key={share.label}
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="body2">
              {share.label}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                · {share.who}
              </Typography>
            </Typography>
            <Switch
              size="small"
              checked={share.on}
              slotProps={{ input: { 'aria-label': share.label, readOnly: true } }}
            />
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}

function Bubble({
  label,
  caption,
  tone,
  big,
}: {
  label: string;
  caption: string;
  tone: 'primary' | 'secondary' | 'accent';
  big?: boolean;
}) {
  const size = big ? 56 : 44;
  return (
    <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
      <Box
        sx={(theme) => ({
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 800,
          fontSize: big ? 16 : 13,
          bgcolor: theme.vars.palette[tone].container,
          color: theme.vars.palette[tone].onContainer,
          boxShadow: big ? `0 0 0 4px ${theme.vars.palette.background.paper}` : 'none',
        })}
      >
        {label}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Stack>
  );
}

function Connector({ active }: { active: boolean }) {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        flex: 1,
        height: 3,
        mx: 1,
        mb: 2.5,
        borderRadius: 2,
        background: active
          ? `linear-gradient(90deg, ${theme.vars.palette.primary.main}, ${theme.vars.palette.accent.main})`
          : theme.vars.palette.divider,
        transition: transition('background', 'long2'),
      })}
    />
  );
}

const MEAL_ITEMS = [
  { name: 'Arroz integral', kcal: 180 },
  { name: 'Feijão carioca', kcal: 120 },
  { name: 'Frango grelhado', kcal: 190 },
  { name: 'Salada verde', kcal: 30 },
];

/** A meal photo turning into items and calories. Illustrative values. */
export function MealAiDemo() {
  const step = useTicker(MEAL_ITEMS.length + 3, 900);
  const shown = Math.max(0, Math.min(step - 1, MEAL_ITEMS.length));
  const total = MEAL_ITEMS.slice(0, shown).reduce((sum, item) => sum + item.kcal, 0);
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'stretch' }}>
      <Box
        aria-hidden
        sx={(theme) => ({
          width: 108,
          flexShrink: 0,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          background: `radial-gradient(circle at 50% 50%, #fff 0 38%, #f1efe9 39% 44%, ${theme.vars.palette.secondary.container} 45%)`,
          '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%' },
          '&::before': { width: 34, height: 26, left: 22, top: 30, background: '#e8d9b5' },
          '&::after': { width: 30, height: 22, left: 52, top: 52, background: '#8d5a3b' },
        })}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 26,
            height: 18,
            left: 30,
            top: 60,
            borderRadius: '50%',
            bgcolor: '#7cb342',
          }}
        />
        <Box
          sx={(theme) => ({
            position: 'absolute',
            insetInline: 0,
            height: 3,
            top: `${((step % 4) / 3) * 100}%`,
            background: theme.vars.palette.accent.main,
            boxShadow: `0 0 16px 4px ${theme.alpha(theme.vars.palette.accent.main, 0.7)}`,
            transition: transition('top', 'long4'),
          })}
        />
      </Box>
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <AutoAwesomeRounded color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2">
            {shown < MEAL_ITEMS.length ? 'Analisando a foto…' : 'Pronto! Confira e salve.'}
          </Typography>
        </Stack>
        {MEAL_ITEMS.map((item, i) => (
          <Stack
            key={item.name}
            direction="row"
            sx={{
              justifyContent: 'space-between',
              opacity: i < shown ? 1 : 0.15,
              transform: i < shown ? 'none' : 'translateX(8px)',
              transition: transition(['opacity', 'transform'], 'medium4'),
            }}
          >
            <Typography variant="body2" noWrap>
              {item.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.kcal} kcal
            </Typography>
          </Stack>
        ))}
        <LinearProgress
          variant="determinate"
          value={(total / 520) * 100}
          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          {total} kcal · o aluno confirma antes de salvar
        </Typography>
      </Stack>
    </Stack>
  );
}

/** The open-request meter people see: what keeps the inbox free of spam. */
export function AntiSpamDemo({ limit = 5 }: { limit?: number }) {
  const step = useTicker(limit + 2, 1100);
  const used = Math.min(step, limit);
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={0.75}>
        {Array.from({ length: limit }, (_, i) => (
          <Box
            key={i}
            sx={(theme) => ({
              flex: 1,
              height: 10,
              borderRadius: 5,
              bgcolor: i < used ? theme.vars.palette.primary.main : theme.vars.palette.divider,
              transition: transition('background-color', 'medium2'),
            })}
          />
        ))}
      </Stack>
      <Typography variant="body2">
        <b>
          {used} de {limit}
        </b>{' '}
        pedidos aguardando resposta
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
        <LockRounded sx={{ fontSize: 16 }} />
        <Typography variant="caption">Seu WhatsApp só aparece para quem você aceitar.</Typography>
      </Stack>
    </Stack>
  );
}

const FREE = [
  'Aparecer na busca e no Google',
  'Receber pedidos de alunos, sem limite',
  'Selo de CREF verificado',
];
const PAID = [
  'App dos seus alunos com o seu nome, suas cores e o seu endereço',
  'Treinos, nutrição, evolução e IA num lugar só',
  'Trabalho em dupla com nutricionista',
];

/** What costs nothing and what is the paid platform: no room for "it's all free". */
export function FreeVsPaid() {
  const column = (title: string, tag: string, items: string[], paid: boolean) => (
    <Box
      sx={(theme) => ({
        flex: 1,
        p: 2.5,
        borderRadius: 3,
        border: `1px solid ${paid ? theme.vars.palette.primary.main : theme.vars.palette.divider}`,
        bgcolor: paid ? theme.alpha(theme.vars.palette.primary.container, 0.5) : 'transparent',
      })}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box
          component="span"
          sx={(theme) => ({
            px: 1.25,
            py: 0.25,
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: paid ? theme.vars.palette.primary.main : theme.vars.palette.success.container,
            color: paid
              ? theme.vars.palette.primary.contrastText
              : theme.vars.palette.success.onContainer,
          })}
        >
          {tag}
        </Box>
      </Stack>
      <Stack component="ul" spacing={0.75} sx={{ m: 0, mt: 1.5, pl: 2.25 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="body2">
            {item}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {column('Perfil no marketplace', 'Grátis', FREE, false)}
        {column('Plataforma com a sua marca', 'Plano pago', PAID, true)}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        O plano é opcional e o perfil continua grátis com ou sem ele. As aulas você cobra do seu
        jeito: o pagamento dos alunos não passa por nós.
      </Typography>
    </Stack>
  );
}
